import { useAdminData } from '../context/AdminDataContext';
import { MousePointerClick, TrendingUp, BarChart2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';

const COLORS = ['#47ace2', '#1f7db3', '#22c55e', '#f59e0b', '#a855f7', '#ef4444'];

export default function CTAPage() {
  const { ctaEvents } = useAdminData();

  const byAction = (ctaEvents || []).reduce((acc, e) => { acc[e.action] = (acc[e.action] || 0) + 1; return acc; }, {});
  const actionData = Object.entries(byAction).map(([name, count]) => ({ name, count }));

  const byPage = (ctaEvents || []).reduce((acc, e) => { acc[e.page] = (acc[e.page] || 0) + 1; return acc; }, {});
  const pageData = Object.entries(byPage).map(([name, value]) => ({ name, value }));

  const hourData = Array.from({ length: 24 }, (_, i) => ({
    hour: `${i}:00`,
    clicks: (ctaEvents || []).filter(e => new Date(e.timestamp || e.created_at).getHours() === i).length,
  })).filter(d => d.clicks > 0);

  const topAction = [...actionData].sort((a, b) => b.count - a.count)[0];
  const topPage = [...pageData].sort((a, b) => b.value - a.value)[0];
  const conversionRate = ctaEvents.length > 0
    ? ((ctaEvents.filter(e => e.action === 'Enquiry Form Submit').length / ctaEvents.length) * 100).toFixed(1)
    : 0;

  const tooltipStyle = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    color: '#1e293b',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  };

  return (
    <div className="p-6 md:p-8 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-outfit">CTA Analytics</h1>
        <p className="text-slate-400 font-manrope text-sm mt-1">Track call-to-action performance and conversions</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { label: 'Total CTA Clicks', value: ctaEvents.length, icon: MousePointerClick, bg: 'bg-[#47ace2]/10', color: 'text-[#47ace2]' },
          { label: 'Top Action', value: topAction?.name || '—', icon: TrendingUp, bg: 'bg-purple-50', color: 'text-purple-500', small: true },
          { label: 'Top Source Page', value: topPage?.name || '—', icon: BarChart2, bg: 'bg-amber-50', color: 'text-amber-500', small: true },
          { label: 'Conversion Rate', value: `${conversionRate}%`, icon: TrendingUp, bg: 'bg-green-50', color: 'text-green-500' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.bg} mb-4`}>
              <card.icon className={`w-5 h-5 ${card.color}`} />
            </div>
            <div className={`font-bold text-slate-800 font-outfit mb-1 ${card.small ? 'text-lg leading-tight' : 'text-3xl'}`}>{card.value}</div>
            <div className="text-slate-400 font-manrope text-sm">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-slate-700 font-semibold font-outfit mb-5">Clicks by Action</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={actionData} barSize={32} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} width={120} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#47ace208' }} />
              <Bar dataKey="count" fill="#47ace2" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-slate-700 font-semibold font-outfit mb-5">Clicks by Source Page</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pageData} cx="50%" cy="45%" outerRadius={80} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={{ stroke: '#cbd5e1' }}
              >
                {pageData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Hourly Trend */}
      {hourData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-slate-700 font-semibold font-outfit mb-5">Hourly Click Trend</h2>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={hourData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="hour" tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="clicks" stroke="#47ace2" strokeWidth={2.5} dot={{ r: 4, fill: '#47ace2', strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Event Log */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-slate-700 font-semibold font-outfit">Recent CTA Events</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-3 text-slate-400 text-xs font-manrope font-semibold uppercase">Action</th>
                <th className="text-left px-5 py-3 text-slate-400 text-xs font-manrope font-semibold uppercase">Source Page</th>
                <th className="text-left px-5 py-3 text-slate-400 text-xs font-manrope font-semibold uppercase">Time</th>
              </tr>
            </thead>
            <tbody>
              {ctaEvents.slice(0, 10).map(e => (
                <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                  <td className="px-5 py-3">
                    <span className="px-2.5 py-1 bg-[#47ace2]/10 text-[#47ace2] text-xs rounded-lg font-manrope font-medium">{e.action}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-600 text-sm font-manrope">{e.page}</td>
                  <td className="px-5 py-3 text-slate-400 text-xs font-manrope">
                    {new Date(e.timestamp || e.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
